import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, LogOut, Menu, User, X } from 'react-feather';
import { ADMIN_NAV, PARTNER_NAV, activeItem } from './nav';
import { SERVERS, getServerKey, setServerKey } from '../lib/servers';
import { useServerKey, useRole } from '../lib/hooks';
import { telcoColor } from '../lib/format';

export function BrandMark({ className = 'hml-brand-mark' }) {
  // Three rising signal bars on a rounded tile.
  return (
    <svg className={className} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#0B7A75" />
      <rect x="8" y="17" width="4" height="7" rx="1.5" fill="#fff" opacity="0.65" />
      <rect x="14" y="12" width="4" height="12" rx="1.5" fill="#fff" opacity="0.85" />
      <rect x="20" y="7" width="4" height="17" rx="1.5" fill="#fff" />
    </svg>
  );
}

function ServerSwitch() {
  const current = useServerKey();
  if (SERVERS.length < 2) return null;
  return (
    <div className="hml-switch" role="group" aria-label="Server to report on">
      {SERVERS.map((s) => (
        <button
          key={s.key}
          type="button"
          aria-pressed={current === s.key}
          onClick={() => setServerKey(s.key)}
          title={`Show reports from the ${s.longLabel}`}
        >
          <span className="hml-dot" style={{ background: telcoColor(s.telcos[0]) }} />
          {s.key === 'airtel' ? (
            <>
              Airtel<span className="hml-switch-long">&nbsp;&amp; 9mobile</span>
            </>
          ) : (
            s.label
          )}
        </button>
      ))}
    </div>
  );
}

function UserMenu() {
  const { user, isAdmin } = useRole();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = String(user?.name || user?.email || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="hml-user" ref={ref}>
      <button type="button" className="hml-user-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="hml-avatar" aria-hidden="true">{initials}</span>
        <span className="hml-user-name">{user?.name}</span>
      </button>
      {open && (
        <div className="hml-menu" role="menu">
          <div className="hml-menu-head">
            <strong>{user?.name}</strong>
            <span>{user?.email}</span>
          </div>
          {isAdmin && user?.id && (
            <Link role="menuitem" to={`/dashboard/usermanager/user/${user.id}`} onClick={() => setOpen(false)}>
              <User /> My account
            </Link>
          )}
          <button type="button" role="menuitem" onClick={() => navigate('/logout')}>
            <LogOut /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function SideFoot() {
  useServerKey();
  const server = SERVERS.find((s) => s.key === getServerKey());
  return <div className="hml-side-foot">Reports from the {server ? server.longLabel : 'MTN server'}</div>;
}

const AppShell = () => {
  const { isAdmin } = useRole();
  const nav = isAdmin ? ADMIN_NAV : PARTNER_NAV;
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const active = useMemo(() => activeItem(nav, pathname), [nav, pathname]);
  const group = useMemo(() => nav.find((g) => g.items.includes(active)), [nav, active]);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.title = active ? `${active.label} | HML Aggregator` : 'HML Aggregator';
  }, [active]);

  return (
    <div className={`hml-shell${open ? ' is-open' : ''}`}>
      <aside className="hml-side" aria-label="Main navigation">
        <Link className="hml-brand" to={isAdmin ? '/dashboard/home' : '/partner/home'}>
          <BrandMark />
          <span>
            <span className="hml-brand-name">HML Aggregator</span>
            <span className="hml-brand-sub">{isAdmin ? 'Operations console' : 'Partner portal'}</span>
          </span>
        </Link>
        <nav className="hml-nav">
          {nav.map((g, i) => (
            <div className="hml-nav-group" key={g.heading || i}>
              {g.heading && <p className="hml-nav-heading">{g.heading}</p>}
              {g.items.map((it) => {
                const Icon = it.icon;
                if (it.href) {
                  return (
                    <a key={it.label} className="hml-nav-link" href={it.href} target="_blank" rel="noreferrer">
                      <Icon aria-hidden="true" />
                      {it.label}
                      <ExternalLink className="hml-nav-ext" aria-label="opens in a new tab" />
                    </a>
                  );
                }
                const isActive = active === it;
                return (
                  <Link
                    key={it.path}
                    to={it.path}
                    className={`hml-nav-link${isActive ? ' is-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon aria-hidden="true" />
                    {it.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <SideFoot />
      </aside>
      <div className="hml-scrim" onClick={() => setOpen(false)} aria-hidden="true" />

      <div className="hml-main">
        <header className="hml-top">
          <button
            type="button"
            className="hml-btn hml-btn--ghost hml-btn--icon hml-menu-btn"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
          <div className="hml-top-title">
            {group?.heading ? `${group.heading} / ` : ''}
            <strong>{active?.label || ''}</strong>
          </div>
          <div className="hml-top-spacer" />
          <ServerSwitch />
          <UserMenu />
        </header>
        <main className="hml-content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
